import type { QuestionOptionDraft } from './questionEditor';
import {
  addOption,
  isSingleCorrectType,
  markCorrectOption,
  removeOption,
  updateOptionContent,
} from './questionEditor';

interface QuestionOptionsSectionProps {
  type: string;
  options: QuestionOptionDraft[];
  onChange: (options: QuestionOptionDraft[]) => void;
}

export function QuestionOptionsSection({ type, options, onChange }: QuestionOptionsSectionProps) {
  const singleCorrect = isSingleCorrectType(type);
  const acceptedAnswers = type === 'short_answer' || type === 'long_answer';
  const lockedSet = type === 'true_false';
  const minCount = acceptedAnswers ? 1 : 2;
  const title = acceptedAnswers ? 'Respuestas aceptadas' : 'Opciones de Respuesta';

  return (
    <section className="lms-question-create__card">
      <h2 className="lms-question-create__card-title">
        <span className="material-symbols-outlined" aria-hidden="true">
          checklist
        </span>
        {title}
      </h2>

      <div className="lms-question-create__options">
        {options.map((opt, index) => (
          <div key={index} className="lms-question-create__option">
            <span className="material-symbols-outlined lms-question-create__drag" aria-hidden="true">
              drag_indicator
            </span>

            {!acceptedAnswers ? (
              <button
                type="button"
                className={`lms-question-create__correct-toggle${opt.is_correct ? ' lms-question-create__correct-toggle--on' : ''}`}
                onClick={() => onChange(markCorrectOption(options, index, singleCorrect))}
                aria-pressed={opt.is_correct}
                title={opt.is_correct ? 'Correcta' : 'Marcar como correcta'}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  {singleCorrect
                    ? opt.is_correct
                      ? 'radio_button_checked'
                      : 'radio_button_unchecked'
                    : opt.is_correct
                      ? 'check_box'
                      : 'check_box_outline_blank'}
                </span>
              </button>
            ) : null}

            <div className="lms-question-create__option-body">
              <input
                type="text"
                className="lms-question-create__option-input"
                value={opt.content}
                onChange={(event) => onChange(updateOptionContent(options, index, event.target.value))}
                placeholder={acceptedAnswers ? 'Respuesta aceptada' : `Opción ${index + 1}`}
                required
                readOnly={type === 'true_false'}
              />
              {opt.is_correct && !acceptedAnswers ? (
                <span className="lms-question-create__correct-badge">Correcta</span>
              ) : null}
            </div>

            {!lockedSet ? (
              <button
                type="button"
                className="lms-question-create__option-delete"
                onClick={() => onChange(removeOption(options, index, minCount))}
                disabled={options.length <= minCount}
                aria-label="Eliminar opción"
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  delete
                </span>
              </button>
            ) : null}
          </div>
        ))}
      </div>

      {!lockedSet ? (
        <button
          type="button"
          className="lms-question-create__add-option"
          onClick={() => onChange(addOption(options, type === 'short_answer'))}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            add
          </span>
          {type === 'short_answer' ? '+ Respuesta' : '+ Opción'}
        </button>
      ) : null}
    </section>
  );
}
